<template>
  <div class="isp-editor" :class="{ desktop: isDesktop }">
    <template v-if="isDesktop">
      <el-radio-group
        v-model="ispModeValue"
        class="isp-editor__modes"
        @update:modelValue="updateIspMode"
      >
        <el-radio-button :value="IspMode.Any">All ISPs</el-radio-button>
        <el-radio-button :value="IspMode.Only">
          Only listed ISPs
        </el-radio-button>
        <el-radio-button :value="IspMode.Except">
          Exclude listed ISPs
        </el-radio-button>
      </el-radio-group>
    </template>
    <template v-else>
      <el-select
        v-model="ispModeValue"
        class="isp-editor__modes"
        @update:modelValue="updateIspMode"
      >
        <el-option label="All ISPs" :value="IspMode.Any" />
        <el-option label="Only listed ISPs" :value="IspMode.Only" />
        <el-option label="Exclude listed ISPs" :value="IspMode.Except" />
      </el-select>
    </template>
    <el-transfer
      v-if="showIspSelector"
      v-model="value"
      label-position="top"
      filterable
      :titles="['All ISPs', 'Selected ISPs']"
      :data="isps"
      @update:modelValue="updateIsp"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, shallowRef, watch } from 'vue'
import useResize from '@/hooks/useResize'
import { useIspsStore } from '@/store/modules/isps'
import { IIsp } from '@/api/isps'
import { ConnectionType, IspMode } from '@/api/types'

type IspEntry = {
  key: string
  label: string
  disabled: boolean
  countryNames: string[]
  connectionTypes: string[]
}

type IspEntryMap = Map<string, IspEntry>

type IspIndex = {
  all: IspEntryMap
  byCountry: Map<string, IspEntryMap>
  byConnectionType: Map<string, IspEntryMap>
  byConnectionTypeAndCountry: Map<string, Map<string, IspEntryMap>>
}

const EmptyIspIndex = (): IspIndex => ({
  all: new Map(),
  byCountry: new Map(),
  byConnectionType: new Map(),
  byConnectionTypeAndCountry: new Map()
})

const IspConnectionTypeByCampaignConnectionType: Partial<
  Record<ConnectionType, string>
> = {
  [ConnectionType.Dialup]: 'cable_dsl',
  [ConnectionType.Cable]: 'cable_dsl',
  [ConnectionType.Corporate]: 'cable_dsl',
  [ConnectionType.Cellular]: 'cellular'
}

export default defineComponent({
  name: 'IspEditor',
  props: {
    modelValue: {
      type: String,
      required: false,
      default: ''
    },
    ispMode: {
      type: String,
      required: false,
      default: IspMode.Any
    },
    countryCodes: {
      type: Array,
      required: false,
      default: () => []
    },
    connectionTypeIds: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  setup(props, context) {
    const ispsStore = useIspsStore()

    const value = ref<string[]>(parseValue(props.modelValue))
    const ispModeValue = ref(props.ispMode as IspMode)
    const isps = ref<IspEntry[]>([])
    const ispIndex = shallowRef<IspIndex>(EmptyIspIndex())

    ispsStore.GetItems().then((items) => {
      ispIndex.value = buildIspIndex(items)
      rebuildIspEntries()
    })

    watch(
      () => props.modelValue,
      (newValue) => {
        value.value = parseValue(newValue)
      }
    )

    watch(
      () => props.ispMode,
      (newValue) => {
        ispModeValue.value = newValue as IspMode
      }
    )

    watch(
      () => ispsStore.sorted,
      (items) => {
        ispIndex.value = buildIspIndex(items)
        rebuildIspEntries()
      },
      { immediate: true }
    )

    watch(
      [() => props.countryCodes, () => props.connectionTypeIds, value],
      rebuildIspEntries,
      { immediate: true, deep: true }
    )

    function parseValue(newValue: unknown): string[] {
      if (Array.isArray(newValue)) {
        return newValue.map((item) => String(item).trim()).filter(Boolean)
      }

      if (
        typeof newValue === 'object' &&
        newValue !== null &&
        'isp' in newValue
      ) {
        return parseValue((newValue as { isp?: unknown }).isp)
      }

      return String(newValue || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
    }

    function stringifyValue(newValue: string[]): string {
      return newValue.join(', ')
    }

    function updateIsp(newValue: string[]) {
      value.value = newValue
      emitUpdate()
    }

    function updateIspMode(newValue: IspMode) {
      ispModeValue.value = newValue
      if (newValue === IspMode.Any) {
        value.value = []
      }
      emitUpdate()
    }

    function emitUpdate() {
      context.emit('update:modelValue', {
        isp:
          ispModeValue.value === IspMode.Any ? '' : stringifyValue(value.value),
        ispMode: ispModeValue.value
      })
    }

    const { isDesktop } = useResize()
    const showIspSelector = computed(() => ispModeValue.value !== IspMode.Any)

    function rebuildIspEntries() {
      const selectedCountryCodes = props.countryCodes.map((code) =>
        String(code).trim().toLowerCase()
      )
      const selectedConnectionTypes = ispConnectionTypesForCampaignTypes()
      const grouped = selectIspEntries(
        selectedCountryCodes,
        selectedConnectionTypes
      )

      addSelectedMissingIsps(grouped)
      isps.value = Array.from(grouped.values())
        .map((item) => ({ ...item, label: ispLabel(item) }))
        .sort((a, b) => a.label.localeCompare(b.label))
    }

    function selectIspEntries(
      countryCodes: string[],
      connectionTypes: string[]
    ): IspEntryMap {
      if (countryCodes.length === 0 && connectionTypes.length === 0) {
        return new Map(ispIndex.value.all)
      }

      const result = new Map<string, IspEntry>()

      if (countryCodes.length > 0 && connectionTypes.length > 0) {
        connectionTypes.forEach((connectionType) => {
          const byCountry =
            ispIndex.value.byConnectionTypeAndCountry.get(connectionType)
          countryCodes.forEach((countryCode) =>
            mergeIspEntries(result, byCountry?.get(countryCode))
          )
        })
        return result
      }

      if (countryCodes.length > 0) {
        countryCodes.forEach((countryCode) =>
          mergeIspEntries(result, ispIndex.value.byCountry.get(countryCode))
        )
        return result
      }

      connectionTypes.forEach((connectionType) =>
        mergeIspEntries(
          result,
          ispIndex.value.byConnectionType.get(connectionType)
        )
      )
      return result
    }

    function buildIspIndex(items: IIsp[]): IspIndex {
      const index = EmptyIspIndex()

      items.forEach((item) => {
        const countryCode = String(item.country_code).trim().toLowerCase()
        const connectionTypes = itemConnectionTypes(item)

        addIspEntry(index.all, item)
        addIspEntry(ispEntryMap(index.byCountry, countryCode), item)

        connectionTypes.forEach((connectionType) => {
          addIspEntry(ispEntryMap(index.byConnectionType, connectionType), item)
          addIspEntry(
            ispEntryMap(
              countryMap(index.byConnectionTypeAndCountry, connectionType),
              countryCode
            ),
            item
          )
        })
      })

      return index
    }

    function ispEntryMap(
      map: Map<string, IspEntryMap>,
      key: string
    ): IspEntryMap {
      if (!map.has(key)) map.set(key, new Map())
      return map.get(key) as IspEntryMap
    }

    function countryMap(
      map: Map<string, Map<string, IspEntryMap>>,
      key: string
    ): Map<string, IspEntryMap> {
      if (!map.has(key)) map.set(key, new Map())
      return map.get(key) as Map<string, IspEntryMap>
    }

    function mergeIspEntries(target: IspEntryMap, source?: IspEntryMap) {
      if (!source) return

      source.forEach((item) => addMergedIspEntry(target, item))
    }

    function addSelectedMissingIsps(grouped: IspEntryMap) {
      value.value
        .filter((ispName) => !grouped.has(ispName))
        .forEach((ispName) => {
          addIspEntry(grouped, {
            country_name: 'Custom',
            country_code: 'legacy',
            isp_name: ispName,
            connection_types: []
          })
        })
    }

    function addIspEntry(grouped: IspEntryMap, item: IIsp) {
      addMergedIspEntry(grouped, {
        key: item.isp_name,
        label: item.isp_name,
        disabled: false,
        countryNames: [item.country_name],
        connectionTypes: itemConnectionTypes(item)
      })
    }

    function addMergedIspEntry(grouped: IspEntryMap, item: IspEntry) {
      const existingEntry = grouped.get(item.key)
      const entry = existingEntry
        ? { ...existingEntry }
        : {
            key: item.key,
            label: item.key,
            disabled: false,
            countryNames: [],
            connectionTypes: []
          }

      entry.countryNames = Array.from(
        new Set([...entry.countryNames, ...item.countryNames])
      )
      entry.connectionTypes = Array.from(
        new Set([...entry.connectionTypes, ...item.connectionTypes])
      )
      grouped.set(item.key, entry)
    }

    function ispConnectionTypesForCampaignTypes(): string[] {
      if (props.connectionTypeIds.length === 0) return []

      return Array.from(
        new Set(
          props.connectionTypeIds
            .map((value) => ispConnectionTypeForCampaignType(value))
            .filter(Boolean) as string[]
        )
      )
    }

    function ispConnectionTypeForCampaignType(
      value: unknown
    ): string | undefined {
      const id = Number(value)
      if (Number.isFinite(id)) {
        return IspConnectionTypeByCampaignConnectionType[id as ConnectionType]
      }

      const name = String(value).trim().toLowerCase()
      if (['dialup', 'cable', 'cable_dsl', 'corporate'].includes(name)) {
        return 'cable_dsl'
      }
      if (['cellular', 'mobile'].includes(name)) return 'cellular'
    }

    function itemConnectionTypes(item: IIsp): string[] {
      return ((item.connection_types || []) as string[])
        .map((connectionType) => String(connectionType).trim().toLowerCase())
        .filter(Boolean)
    }

    function ispLabel(item: IspEntry): string {
      const countries = item.countryNames.filter(
        (countryName) => countryName !== 'Custom'
      )
      if (countries.length === 0) return item.key
      if (countries.length === 1) return `${countries[0]} - ${item.key}`
      return `${item.key} (${countries.length} countries)`
    }

    return {
      IspMode,
      updateIsp,
      updateIspMode,
      value,
      isDesktop,
      ispModeValue,
      showIspSelector,
      isps
    }
  }
})
</script>

<style lang="scss" scoped>
.isp-editor {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.isp-editor__modes {
  align-self: flex-start;
}

.el-transfer {
  margin-top: 1em;
}

.el-transfer :deep(.el-transfer-panel) {
  width: 100%;
}

.desktop .el-transfer :deep(.el-transfer-panel) {
  width: 250px;
}

.isp-editor :deep(.el-transfer .el-input) {
  width: 100% !important;
  max-width: 100% !important;
}

.el-transfer :deep(.el-transfer-panel__item) {
  display: block;
}

.desktop .el-transfer :deep(.el-transfer__buttons) {
  width: auto;
}

.desktop .el-transfer :deep(.el-checkbox__label) {
  font-size: 10px;
}

.el-transfer :deep(.el-transfer__buttons) {
  width: 100%;
  text-align: center;
  margin: 1em 0;

  button {
    margin-bottom: 0;
  }
}
</style>
